import axios from 'axios';
import { store } from '../redux/store';
import { logout, loginSuccess, getStoredToken, getStoredAuth, USER_TYPE } from '../redux/slices/authSlice';
import { PostRefreshToken, ChatBaseUrl } from './Api';

const http = axios.create();

const currentToken = () => store.getState()?.auth?.token || getStoredToken();

const entityScope = () => {
    const state = store.getState();
    if (state?.auth?.user?.userTypeId !== USER_TYPE.MASTER_ADMIN) return null;
    const id = state?.org?.activeEntityId;
    return /^\d+$/.test(String(id ?? '')) ? String(id) : null;
};

const headerSet = (headers, name, value) => {
    if (typeof headers?.set === 'function') headers.set(name, value);
    else headers[name] = value;
};
const headerHas = (headers, name) => (
    typeof headers?.has === 'function' ? headers.has(name) : headers?.[name] != null
);

http.interceptors.request.use((config) => {
    const token = currentToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;

    if (!config.noEntityScope && !headerHas(config.headers, 'X-Entity-Id')) {
        const entityId = entityScope();
        if (entityId != null) headerSet(config.headers, 'X-Entity-Id', entityId);
    }
    return config;
});

let refreshPromise = null;

const runRefresh = () => {
    if (refreshPromise) return refreshPromise;
    const auth = store.getState()?.auth || {};
    const stored = getStoredAuth() || {};
    const refreshToken = auth.refreshToken || stored.refreshToken;
    const refreshExp = auth.refreshTokenExpiresOn || stored.refreshTokenExpiresOn;
    if (!refreshToken || (refreshExp && new Date(refreshExp).getTime() <= Date.now())) {
        return Promise.reject(new Error('no-refresh-token'));
    }
    refreshPromise = axios.post(PostRefreshToken, { refreshToken })
        .then((res) => {
            const data = res?.data?.data;
            if (res?.data?.error || !data?.token) throw new Error('refresh-failed');
            store.dispatch(loginSuccess({ ...data, remember: auth.remember !== false }));
            return data.token;
        })
        .finally(() => { refreshPromise = null; });
    return refreshPromise;
};

const bounceToDashboard = () => {
    try { window.dispatchEvent(new CustomEvent('ara:module-denied')); } catch { }
};

http.interceptors.response.use(
    (res) => res,
    async (error) => {
        const status = error?.response?.status;
        const original = error?.config;
        const isAuthed = store.getState()?.auth?.isAuthenticated;

        if (status === 401 && isAuthed && original && !original._retry) {
            original._retry = true;
            try {
                await runRefresh();
                return http(original);
            } catch {
                // Refresh itself failed — the session is dead for every caller,
                // noAuthBounce included.
                store.dispatch(logout());
                return Promise.reject(error);
            }
        }
        if (status === 401 && isAuthed) {
            // The token refreshed fine and the endpoint still says no: it 401s by
            // design for this role (e.g. the self-service PunchRequest calls need
            // an employee profile the MasterAdmin doesn't have). Reject quietly
            // rather than ending a perfectly good session.
            if (original?.noAuthBounce) return Promise.reject(error);
            // The chat service is a different server: if it refuses a token the payroll server just refreshed,
            // that is chat's problem, not a dead session. Sending the user to login here is what made the
            // Chat page load and then bounce to the login screen.
            if (typeof original?.url === 'string' && ChatBaseUrl && original.url.startsWith(ChatBaseUrl)) return Promise.reject(error);
            store.dispatch(logout());
            return Promise.reject(error);
        }

        if (status === 403 && error?.response?.data?.code === 'MODULE_ACCESS_DENIED') {
            try { await runRefresh(); } catch { }
            bounceToDashboard();
            return Promise.reject(error);
        }

        return Promise.reject(error);
    },
);

export const apiErrorMessage = (error, fallback = 'Something went wrong. Please try again.') => {
    if (error?.response) {
        const d = error.response.data;
        return (
            d?.message
            || d?.error?.message
            || (typeof d?.error === 'string' ? d.error : null)
            || d?.title
            || (error.response.status === 401 ? 'Invalid login ID or password.' : null)
            || fallback
        );
    }
    if (error?.code === 'ECONNABORTED') return 'The server took too long to respond. Please try again.';
    if (error?.request) return 'Cannot reach the server. Check your connection and try again.';
    return fallback;
};

export default http;
